import React, { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import Icon from '../../components/icons'
import Button from '../../elements/button'
import P from '../../elements/Typo/P'
import Text from '../../elements/Typo/Text'
import CustomAgGrid from '../../components/CustomAgGrid'
import { AgGridReact } from 'ag-grid-react'
import { useExperimentSelector } from '../../store/selectors/experimentSelector'
import i18n from '../../i18n'
import ModalCloseIconButton from '../modalCloseIconButton'

interface UploadModalProps {
    _ref: any
    filesucess: any
    uploadfilename: string
    onContinue: () => void
    onReset: () => void
}

const UploadSucessModal: React.FC<UploadModalProps> = ({ _ref, filesucess, uploadfilename, onContinue, onReset }) => {
    const { t } = useTranslation()
    const dataTableGrid = useRef<AgGridReact>(null)
    const experiment = useExperimentSelector()

    const [isDisabled, setIsDisabled] = useState<boolean>(true)
    const [errorMessages, setErrorMessages] = useState<string[]>([])

    const rowData = filesucess?.numeric_statistic
    const rowHeight = 60
    const headerHeight = 50
    const maxRows = 10

    const gridHeight = Math.min(rowData?.length, maxRows) * rowHeight + headerHeight
    const cellRenderer = (value: any) => {
        return (
            <div className="flex items-center justify-center pt-2 w-full pr-1">
                <Text size="lg1" className="text-wrap truncate " title={value ?? '-'}>
                    {value ?? '-'}
                </Text>
            </div>
        )
    }

    const columnDefs = [
        {
            headerName: t('Numerical Column'),
            field: 'column_name',

            sortable: false,
            filter: false,
            width: 150,
            cellStyle: {
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
            },
            tooltipField: 'column_name',
            cellRenderer: (params: any) => cellRenderer(params?.data?.column_name ?? '-'),
        },
        {
            headerName: t('Mean'),
            field: 'mean',
            filter: false,
            sortable: true,
            width: 100,
            cellStyle: {
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
            },
            tooltipField: 'mean',
            cellRenderer: (params: any) => cellRenderer(params?.data?.mean ?? '-'),
        },
        {
            headerName: t('Min'),
            field: 'min',
            filter: false,
            sortable: true,
            width: 100,
            cellStyle: {
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
            },
            tooltipField: 'min',
            cellRenderer: (params: any) => cellRenderer(params?.data?.min ?? '-'),
        },
        {
            headerName: t('Max'),
            field: 'max',

            filter: false,
            sortable: true,
            width: 100,
            cellStyle: {
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
            },
            tooltipField: 'max',
            cellRenderer: (params: any) => cellRenderer(params?.data?.max ?? '-'),
        },
    ]

    const closeModal = (reset: boolean = false) => {
        if (reset) {
            onReset()
        }
        _ref.current.close()
    }

    useEffect(() => {
        const validationParams: string[] = ((filesucess?.['validations'] && Object.keys(filesucess['validations'])) || []).filter((param: string) => param !== 'has_missing_values')

        const messages: string[] = validationParams.map((param: string) => filesucess['validations'][param]?.msg ?? '').filter(Boolean)
        let isNoError: boolean

        if (experiment?.experiment_type?.title === 'Post-Treatment Comparison') {
            isNoError = validationParams.map((param: string) => (param === 'has_enough_date_columns' ? true : filesucess['validations'][param]?.status)).every((value: boolean) => value === true)
        } else {
            isNoError = validationParams.map((param: string) => filesucess['validations'][param]?.status).every((value: boolean) => value === true)
        }

        setIsDisabled(!isNoError)
        setErrorMessages(messages.length ? messages : [])
        if (experiment?.experiment_type?.title == 'Observational Study') {
            if (filesucess?.numeric_statistic?.length == 0) {
                setErrorMessages([...errorMessages, t('Numeric column missing')])
                setIsDisabled(true)
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filesucess])

    return (
        <dialog ref={_ref} className="modal">
            <div className="modal-box w-[26.583rem]">
                <ModalCloseIconButton
                    onClick={() => {
                        closeModal(false)
                    }}
                />

                <P className="font-semibold text-center" data-testid="modal-title">
                    {t('Data Quality Report')}
                </P>
                <div className="flex relative items-center justify-between border-[0.025rem] rounded-[0.147rem] p-2 border-[#E3E3E3]">
                    <div className="w-full">
                        <Text size="sm" className="w-full [overflow-wrap:anywhere] whitespace-pre-wrap">
                            {uploadfilename}
                        </Text>
                    </div>
                </div>
                <div className="p-[0.625rem] bg-[#fdfdf2] my-4 rounded-md border border-[#F9EACE]">
                    <Text size="lg" className="font-semibold">
                        {t('Missing Values')}
                    </Text>
                    <div className="border-t border-[#F9EACE] pt-2">
                        <div className="flex gap-2 flex-wrap">
                            {filesucess?.missing_values_column.map((missing_values: any, index: number) => (
                                <div data-testid="missing-pill" key={`missing-col-${missing_values}`} className="bg-[#EFEFEF] h-[0.792rem] leading-none rounded-[1.667rem]  px-[0.333rem] text-[0.458rem] flex items-center">
                                    {missing_values}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
                <div className="p-[0.625rem] bg-[#fdfdf2] my-4 rounded-md border border-[#F9EACE]">
                    <Text size="lg" className="font-semibold">
                        {t('Repeated Values')}
                    </Text>
                    <div className="border-t border-[#F9EACE] pt-2">
                        <div className="flex gap-2 flex-wrap">
                            {filesucess?.repeated_values_columns.map((repeated_values: any, index: number) => (
                                <div key={repeated_values} data-testid="repeated-pill" className="bg-[#EFEFEF] h-[0.792rem] leading-none rounded-[1.667rem]  px-[0.333rem] text-[0.458rem] flex items-center">
                                    {repeated_values}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
                <div className="p-[0.625rem] bg-[#fdfdf2] my-4 rounded-md border border-[#F9EACE]">
                    <Text size="lg" className="font-semibold">
                        {t('Invalid Values (inf, NaN)')}
                    </Text>
                    <div className="border-t border-[#F9EACE] pt-2">
                        <div className="flex gap-2 flex-wrap">
                            {filesucess?.invalid_values.map((invalid_value: any) => (
                                <div key={invalid_value?.name} data-testid="invalid-pill" className="bg-[#EFEFEF] h-[0.792rem] leading-none rounded-[1.667rem]  px-[0.333rem] text-[0.458rem] flex items-center">
                                    {invalid_value?.name}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
                <div>
                    <CustomAgGrid
                        key={'usuccess' + i18n.language}
                        gridRef={dataTableGrid}
                        rowData={rowData}
                        columnDefs={columnDefs}
                        defaultColDef={{ sortable: true, filter: true }}
                        theme="light"
                        pagination={false}
                        paginationPageSize={5} // Must match one of the values below
                        paginationPageSizeSelector={[5, 10, 20, 50]} // Ensure 5 is included
                        style={{ height: gridHeight, width: '100%' }}
                        gridId="summmyGrid"
                        rowHeight={50}
                        data-testid="custom-grid"
                        alternativeRowColor={'#E5B61112'}
                    />
                </div>
                <div className="my-4">
                    {errorMessages.map((message: string) => (
                        <div key={message} className="flex gap-1">
                            <Icon size={'0.8'} name="warning" className="text-yellow-700" />
                            <Text size="sm" theme="neutral" className="text-yellow-700">
                                {message}
                            </Text>
                        </div>
                    ))}
                </div>
                {filesucess?.invalid_values.length > 0 && (
                    <div className="my-4">
                        <div className="flex gap-1">
                            <Icon size={'0.8'} name="warning" className="text-yellow-700" />
                            <Text size="sm" className="text-yellow-700">
                                {t('Your file contains null values, which may impact experiment quality. Proceeding is allowed, but reviewing null values is advised.')}
                            </Text>
                        </div>
                    </div>
                )}
                <div>
                    <div className="grid grid-cols-2 gap-2 mt-3" data-testid="Upload-Again">
                        <Button
                            theme="primary"
                            onClick={() => {
                                closeModal(true)
                            }}
                            size="sm"
                            data-testid="upload-again"
                        >
                            {t('Upload Again')}
                        </Button>{' '}
                        <Button theme="secondary" disabled={isDisabled} onClick={isDisabled ? () => {} : onContinue} size="sm" data-testid="continue-button">
                            {t('Continue')}
                        </Button>
                    </div>
                </div>
            </div>
            <form method="dialog" className="modal-backdrop"></form>
        </dialog>
    )
}

export default UploadSucessModal
